'use client'

import { useLocale, useTranslations } from 'next-intl'
import { useParams, useSearchParams } from 'next/navigation'
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react'
import useSWR, { mutate } from 'swr'

import { useAuthContext } from '@/context/AuthContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useUAContext } from '@/context/UAContext'
import { useEnableFull3d } from '@/hooks/useEnableFull3d'
import { useIsEmbedded } from '@/hooks/useIsEmbedded'
import { isViverseProOwner } from '@/lib/cms'
import { formatNumberWithKilo } from '@/lib/format'
import { showToast } from '@/lib/toast'
import {
  checkUserVoucher,
  getRoomPageData,
  postLikeRoom,
  postSaveRoom,
  postUnlikeRoom,
  postUnsaveRoom,
  RoomPageData,
  UserProfile,
} from '@/services/cms'
import { DeviceTypeWithAndroidIOS } from '@/types/configs'

function resolveAgeGateStatus({
  isAdultOnly,
  isOwner,
  isSignedIn,
  isOver18,
}: {
  isAdultOnly: boolean
  isOwner: boolean
  isSignedIn: boolean
  isOver18: boolean | null
}): AgeGateStatus {
  if (!isAdultOnly || isOwner) return AGE_GATE_STATUS.NONE
  if (!isSignedIn) return AGE_GATE_STATUS.NOT_SIGNED_IN
  if (isOver18 === null) return AGE_GATE_STATUS.NO_BIRTHDAY
  if (!isOver18) return AGE_GATE_STATUS.UNDERAGE
  return AGE_GATE_STATUS.NONE
}

export const AGE_GATE_STATUS = {
  NONE: 'none',
  NOT_SIGNED_IN: 'not-signed-in',
  NO_BIRTHDAY: 'no-birthday',
  UNDERAGE: 'underage',
} as const

export type AgeGateStatus = (typeof AGE_GATE_STATUS)[keyof typeof AGE_GATE_STATUS]

type CheckEnterRoomContextType = {
  roomData: RoomPageData
  isOwnerViversePro: boolean
  isOwner: boolean
  isCoOwner: boolean
  canPreview: boolean
  isLikeButtonEnabled: boolean
  isSaveButtonEnabled: boolean
  setRoomData: Dispatch<SetStateAction<RoomPageData>>
  toggleLikeRoom: () => Promise<void>
  toggleSaveRoom: () => Promise<void>
  viewCountWithKilo: string
  likeCountWithKilo: string
  preferDevices: DeviceTypeWithAndroidIOS[]
  isPreferredDevice: boolean
  isTrailer: boolean
  setIsTrailer: Dispatch<SetStateAction<boolean>>
  ageGateStatus: AgeGateStatus
  isEmbeddedAdultBlocked: boolean
  isPremiumRoom: boolean
  isVoucherCheckPassed?: boolean
  shouldShowMobileTip: boolean
  shouldShowTrailerThumbnails: boolean
  isPartnerPromise: Promise<boolean>
  guestAccountPromise: Promise<UserProfile | undefined>
}

export const CheckEnterRoomContext = createContext<CheckEnterRoomContextType | null>(null)

type CheckEnterRoomProviderProps = PropsWithChildren<{
  initialRoomData: RoomPageData
  isOwner: boolean
  isCoOwner: boolean
  isPartnerPromise: Promise<boolean>
  guestAccountPromise: Promise<UserProfile | undefined>
  canPreview: boolean
  isEmbeddedAdultBlockedFromServer?: boolean
}>

export function CheckEnterRoomProvider({
  children,
  initialRoomData,
  canPreview,
  isOwner,
  isCoOwner,
  isPartnerPromise,
  guestAccountPromise,
  isEmbeddedAdultBlockedFromServer = false,
}: CheckEnterRoomProviderProps) {
  const { roomId } = useParams<{ roomId: string }>()
  const { store } = useConfigsContext()
  const { isSignedIn, isOver18, handleLogin } = useAuthContext()
  const { deviceTypeWithAndroidAndIOS, isMobile } = useUAContext()
  const enableFull3d = useEnableFull3d()
  const locale = useLocale()
  const searchParams = useSearchParams()
  const t = useTranslations()
  const version = canPreview ? (searchParams.get('version') ?? undefined) : undefined

  const htcToken = store?.credentials?.htcToken
  const profileId = store?.profile?.id

  const [roomData, setRoomData] = useState(initialRoomData)
  const [isLikeButtonEnabled, setIsLikeButtonEnabled] = useState(!htcToken ? true : false)
  const isOwnerViversePro = isViverseProOwner(roomData.owner)
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(!htcToken ? true : false)

  const viewCountWithKilo = formatNumberWithKilo(roomData.view_count)
  const likeCountWithKilo = formatNumberWithKilo(roomData.like_count)
  const preferDevices = roomData.preferred_devices || []
  const isPreferredDevice = preferDevices.includes(deviceTypeWithAndroidAndIOS)

  const [isTrailer, setIsTrailer] = useState(false)

  const isAdultOnly = !!roomData?.is_adult_only

  const isEmbeddedFromClient = useIsEmbedded()

  const isEmbeddedAdultBlocked =
    isEmbeddedAdultBlockedFromServer || (isAdultOnly && isEmbeddedFromClient)

  const isOwnerOrCoOwner = isOwner || isCoOwner

  const ageGateStatus = useMemo(
    () =>
      isEmbeddedAdultBlocked
        ? AGE_GATE_STATUS.NONE
        : resolveAgeGateStatus({ isAdultOnly, isOwner: isOwnerOrCoOwner, isSignedIn, isOver18 }),
    [isEmbeddedAdultBlocked, isAdultOnly, isOwnerOrCoOwner, isSignedIn, isOver18],
  )

  const isPremiumRoom = !!roomData?.is_voucher_room
  const shouldShowMobileTip = !enableFull3d && isMobile && !roomData?.vod_id
  const shouldShowTrailerThumbnails = !enableFull3d && (isPremiumRoom || !isMobile)
  const shouldCheckVoucher = isPremiumRoom && !!profileId && !!roomData.hub_sid

  const { data: isVoucherPassedFromApi } = useSWR<boolean>(
    shouldCheckVoucher ? 'checkUserVoucher' : null,
    () => checkUserVoucher(roomData.hub_sid, profileId!),
  )

  const isVoucherCheckPassed =
    canPreview && isPremiumRoom
      ? true
      : shouldCheckVoucher
        ? isVoucherPassedFromApi
        : !isPremiumRoom

  // 🥷🏼 Do again here for token with data
  useSWR(
    htcToken ? ['checkEnterRoomWithToken', roomId, locale, version] : null,
    () => getRoomPageData(roomId, htcToken, false, locale, version),
    {
      onSuccess: (data) => {
        if (!data) return

        setRoomData(data)
        setIsLikeButtonEnabled(true)
        setIsSaveButtonEnabled(true)
      },
    },
  )

  const toggleLikeRoom = async () => {
    if (!isSignedIn) {
      handleLogin()
      return
    }

    if (!roomData.id) return
    const isLiked = roomData.is_liked
    setIsLikeButtonEnabled(false)

    try {
      const result = isLiked
        ? await postUnlikeRoom(roomData.id, htcToken!)
        : await postLikeRoom(roomData.id, htcToken!)

      if (result?.status !== 200) {
        throw new Error('Failed to toggle like room status')
      }

      setRoomData((prev) => ({
        ...prev,
        is_liked: !isLiked,
        like_count: result.results.like_count,
      }))
    } catch (error) {
      console.error('toggle like room error:', error)
    } finally {
      setIsLikeButtonEnabled(true)
    }
  }

  const toggleSaveRoom = async () => {
    if (!isSignedIn) {
      handleLogin()
      return
    }

    if (!roomData.id) return
    const isSaved = roomData.is_saved
    setIsSaveButtonEnabled(false)

    setRoomData((prev) => ({ ...prev, is_saved: !isSaved }))

    try {
      const result = isSaved
        ? await postUnsaveRoom(roomData.id, htcToken!)
        : await postSaveRoom(roomData.id, htcToken!)

      if (result?.status === 409) {
        setRoomData((prev) => ({ ...prev, is_saved: isSaved }))
        const body = result.results as unknown as { message?: string }
        showToast({
          type: 'error',
          message: body?.message || t('worlds-landing.saved.save-cap-error'),
        })
        return
      }

      if (result?.status !== 200) {
        setRoomData((prev) => ({ ...prev, is_saved: isSaved }))
        throw new Error('Failed to toggle save room status')
      }

      if (!isSaved) {
        showToast({ type: 'success', message: t('worlds-landing.saved.toast.saved') })
      }

      void mutate((key) => typeof key === 'string' && key.startsWith('savedRooms'), undefined, {
        revalidate: true,
      })
    } catch (error) {
      setRoomData((prev) => ({ ...prev, is_saved: isSaved }))
      console.error('toggle save room error:', error)
      showToast({ type: 'error', message: t('worlds-landing.saved.toast.save-error') })
    } finally {
      setIsSaveButtonEnabled(true)
    }
  }

  return (
    <CheckEnterRoomContext.Provider
      value={{
        roomData,
        isOwnerViversePro,
        isOwner,
        isCoOwner,
        canPreview,
        isLikeButtonEnabled,
        isSaveButtonEnabled,
        setRoomData,
        toggleLikeRoom,
        toggleSaveRoom,
        viewCountWithKilo,
        likeCountWithKilo,
        preferDevices,
        isPreferredDevice,
        isTrailer,
        setIsTrailer,
        ageGateStatus,
        isEmbeddedAdultBlocked,
        isPremiumRoom,
        isVoucherCheckPassed,
        shouldShowMobileTip,
        shouldShowTrailerThumbnails,
        isPartnerPromise,
        guestAccountPromise,
      }}
    >
      {children}
    </CheckEnterRoomContext.Provider>
  )
}

export const useCheckEnterRoomContext = () => {
  const value = useContext(CheckEnterRoomContext)

  if (value == null) {
    throw new Error('useCheckEnterRoomContext cannot be used outside of CheckEnterRoomProvider')
  }

  return value
}
